import type {
  TweetReply,
  TokenShillNetwork,
  PumpDumpIndicator,
  SuspiciousTokenAlert,
  BotAccount
} from '@podx/core/types';
import { config } from '@podx/core/config';
import { logger } from '@podx/core/logger';
import { BotTracker } from './BotTracker.js';

/**
 * PumpDumpDetector - Detects coordinated pump and dump schemes
 * 
 * This analyzer identifies patterns in bot behavior and token mentions
 * that indicate coordinated market manipulation campaigns
 */
export class PumpDumpDetector {
  private tokenNetworks = new Map<string, TokenShillNetwork>();
  private activeAlerts = new Map<string, SuspiciousTokenAlert>();
  private botTracker: BotTracker;

  // Time thresholds for different campaign types
  private readonly PUMP_CAMPAIGN_MAX_DURATION = 72 * 60 * 60 * 1000; // 72 hours
  private readonly COORDINATED_BURST_WINDOW = 60 * 60 * 1000; // 1 hour
  private readonly SHILL_BURST_THRESHOLD = 5; // mentions per hour to trigger alert

  constructor(botTracker: BotTracker) {
    this.botTracker = botTracker;
    
    logger.info('PumpDumpDetector initialized', {
      operation: 'pump_dump_detector_init'
    });
  }

  /**
   * Analyze token shilling patterns across all bot accounts
   */
  analyzeTokenShillingPatterns(replies: TweetReply[]): {
    suspiciousNetworks: TokenShillNetwork[];
    activeAlerts: SuspiciousTokenAlert[];
    riskTokens: Array<{token: string; riskLevel: number; indicators: string[]}>;
  } {
    logger.info('Starting pump/dump pattern analysis', {
      operation: 'analyze_pump_dump_patterns',
      replyCount: replies.length
    });

    // Update token networks with new data
    this.updateTokenNetworks(replies);

    // Detect coordinated campaigns
    const suspiciousNetworks = this.detectCoordinatedCampaigns();

    // Generate alerts for high-risk tokens
    const activeAlerts = this.generateTokenAlerts(suspiciousNetworks);

    // Calculate risk levels for all tokens
    const riskTokens = this.calculateTokenRiskLevels();

    // Update active alerts
    for (const alert of activeAlerts) {
      this.activeAlerts.set(alert.tokenSymbol, alert);
    }

    logger.info('Pump/dump analysis complete', {
      operation: 'pump_dump_analysis_complete',
      suspiciousNetworks: suspiciousNetworks.length,
      activeAlerts: activeAlerts.length,
      riskTokens: riskTokens.length
    });

    return {
      suspiciousNetworks,
      activeAlerts,
      riskTokens
    };
  }

  private updateTokenNetworks(replies: TweetReply[]): void {
    const tokenMentions = new Map<string, Array<{reply: TweetReply; username: string}>>();

    // Group replies by token mentions
    for (const reply of replies) {
      const tokens = this.extractTokens(reply.text);
      for (const token of tokens) {
        if (!tokenMentions.has(token)) {
          tokenMentions.set(token, []);
        }
        tokenMentions.get(token)!.push({reply, username: reply.username});
      }
    }

    // Update or create token networks
    for (const [tokenSymbol, mentions] of tokenMentions) {
      this.updateTokenNetwork(tokenSymbol, mentions);
    }
  }

  private updateTokenNetwork(
    tokenSymbol: string, 
    mentions: Array<{reply: TweetReply; username: string}>
  ): void {
    let network = this.tokenNetworks.get(tokenSymbol);
    const now = new Date();

    if (!network) {
      // Create new token network
      network = {
        tokenSymbol,
        contractAddress: this.extractContractFromMentions(mentions),
        networkAccounts: [],
        campaignStart: now,
        campaignType: 'long_term_shill',
        indicators: [],
        totalMentions: 0,
        peakActivity: now,
        suspicionLevel: 0
      };
    }

    // Update network with new mentions
    const newAccounts = mentions.map(m => m.username);
    network.networkAccounts = [...new Set([...network.networkAccounts, ...newAccounts])];
    network.totalMentions += mentions.length;

    // Analyze campaign type and indicators
    const campaignAnalysis = this.analyzeCampaignType(tokenSymbol, mentions);
    network.campaignType = campaignAnalysis.type;
    network.indicators.push(...campaignAnalysis.indicators);

    // Calculate suspicion level
    network.suspicionLevel = this.calculateNetworkSuspicion(network, mentions);

    // Detect peak activity
    if (mentions.length > 0) {
      network.peakActivity = now;
    }

    this.tokenNetworks.set(tokenSymbol, network);

    logger.debug('Updated token network', {
      operation: 'update_token_network',
      token: tokenSymbol,
      accountCount: network.networkAccounts.length,
      totalMentions: network.totalMentions,
      suspicionLevel: network.suspicionLevel
    });
  }

  private extractTokens(text: string): string[] {
    const tokens: string[] = [];
    const tokenPattern = /\$([A-Z]{2,10})/g;
    let match;

    while ((match = tokenPattern.exec(text)) !== null) {
      const token = match[1];
      // Filter out common false positives
      if (!this.isCommonCurrency(token)) {
        tokens.push(token);
      }
    }

    return [...new Set(tokens)];
  }

  private isCommonCurrency(token: string): boolean {
    const commonCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF'];
    return commonCurrencies.includes(token);
  }

  private extractContractFromMentions(mentions: Array<{reply: TweetReply; username: string}>): string | undefined {
    for (const {reply} of mentions) {
      const contract = this.extractContractAddress(reply.text);
      if (contract) return contract;
    }
    return undefined;
  }

  private extractContractAddress(text: string): string | undefined {
    // Ethereum contract pattern
    const ethPattern = /0x[a-fA-F0-9]{40}/;
    const ethMatch = text.match(ethPattern);
    if (ethMatch) return ethMatch[0];

    // Solana contract pattern
    const solPattern = /[1-9A-HJ-NP-Za-km-z]{32,44}/;
    const solMatch = text.match(solPattern);
    if (solMatch) return solMatch[0];

    return undefined;
  }

  private analyzeCampaignType(
    tokenSymbol: string,
    mentions: Array<{reply: TweetReply; username: string}>
  ): {type: TokenShillNetwork['campaignType']; indicators: PumpDumpIndicator[]} {
    const indicators: PumpDumpIndicator[] = [];
    let campaignType: TokenShillNetwork['campaignType'] = 'long_term_shill';

    // Analyze timing patterns
    const timestamps = mentions.map(m => new Date(m.reply.createdAt).getTime()).sort();
    const timeSpan = timestamps[timestamps.length - 1] - timestamps[0];

    // Check for coordinated bursts (pump and dump indicator)
    const burstMentions = this.detectBurstMentions(mentions);
    if (burstMentions > this.SHILL_BURST_THRESHOLD) {
      campaignType = 'pump_and_dump';
      indicators.push({
        type: 'shill_burst',
        confidence: Math.min(1, burstMentions / 20),
        evidence: [`${burstMentions} mentions in short time window`],
        timestamp: new Date(),
        accountsInvolved: mentions.slice(0, 10).map(m => m.username)
      });
    }

    // Detect urgency patterns (pump campaign)
    const urgentMentions = mentions.filter(m => 
      this.containsUrgencyLanguage(m.reply.text)
    );
    
    if (urgentMentions.length > mentions.length * 0.4) {
      campaignType = 'pump_and_dump';
      indicators.push({
        type: 'fake_urgency',
        confidence: urgentMentions.length / mentions.length,
        evidence: [`${urgentMentions.length}/${mentions.length} mentions contain urgency language`],
        timestamp: new Date(),
        accountsInvolved: urgentMentions.map(m => m.username)
      });
    }

    // Detect exit/dump signals
    const dumpSignals = mentions.filter(m => 
      this.containsDumpLanguage(m.reply.text)
    );

    if (dumpSignals.length > 0) {
      indicators.push({
        type: 'exit_signals',
        confidence: 0.9,
        evidence: [`${dumpSignals.length} dump/exit signals detected`],
        timestamp: new Date(),
        accountsInvolved: dumpSignals.map(m => m.username)
      });
    }

    // Detect rugpull preparation patterns
    const rugPullPatterns = mentions.filter(m => 
      /(?:team|dev|founder).*(?:selling|dumping|exit)/i.test(m.reply.text) ||
      /(?:rug|scam|avoid|stay\s+away)/i.test(m.reply.text)
    );

    if (rugPullPatterns.length > 0) {
      campaignType = 'rugpull_preparation';
      indicators.push({
        type: 'exit_signals',
        confidence: 0.95,
        evidence: ['Rugpull warning patterns detected'],
        timestamp: new Date(),
        accountsInvolved: rugPullPatterns.map(m => m.username)
      });
    }

    return { type: campaignType, indicators };
  }

  private detectBurstMentions(mentions: Array<{reply: TweetReply; username: string}>): number {
    const now = Date.now();
    const oneHourAgo = now - this.COORDINATED_BURST_WINDOW;

    return mentions.filter(m => 
      new Date(m.reply.createdAt).getTime() > oneHourAgo
    ).length;
  }

  private containsUrgencyLanguage(text: string): boolean {
    const urgencyPatterns = [
      /(?:now|asap|quick|fast|hurry|urgent|immediately)/i,
      /(?:last\s+chance|limited\s+time|ending\s+soon|act\s+fast)/i,
      /(?:only|just)\s+\d+\s+(?:hours?|minutes?|days?)\s+left/i,
      /(?:don\'?t\s+miss|grab\s+now|buy\s+now)/i
    ];

    return urgencyPatterns.some(pattern => pattern.test(text));
  }

  private containsDumpLanguage(text: string): boolean {
    const dumpPatterns = [
      /(?:sell|exit|dump|take\s+profits?)/i,
      /(?:peaked|top|resistance|sell\s+zone)/i,
      /(?:warning|danger|avoid|rug|scam)/i,
      /(?:team|dev).*(?:selling|dumping)/i
    ];

    return dumpPatterns.some(pattern => pattern.test(text));
  }

  private calculateNetworkSuspicion(
    network: TokenShillNetwork, 
    recentMentions: Array<{reply: TweetReply; username: string}>
  ): number {
    let suspicion = 0;

    // Factor 1: Number of bot accounts involved
    const botAccounts = network.networkAccounts.filter(username => {
      const botData = this.botTracker.getBotAccount(username);
      return botData && botData.reputation !== 'bot';
    });
    const botRatio = botAccounts.length / Math.max(network.networkAccounts.length, 1);
    suspicion += botRatio * 0.4;

    // Factor 2: Mention frequency and burst activity
    const mentionRate = network.totalMentions / Math.max(network.networkAccounts.length, 1);
    if (mentionRate > 3) suspicion += 0.2;
    if (mentionRate > 10) suspicion += 0.2;

    // Factor 3: Campaign indicators
    const indicatorWeight = network.indicators.reduce((sum, indicator) => 
      sum + indicator.confidence, 0
    ) / Math.max(network.indicators.length, 1);
    suspicion += indicatorWeight * 0.3;

    // Factor 4: Recent activity burst
    const recentBurst = this.detectBurstMentions(recentMentions);
    if (recentBurst > this.SHILL_BURST_THRESHOLD) {
      suspicion += 0.1;
    }

    return Math.min(1, suspicion);
  }

  private detectCoordinatedCampaigns(): TokenShillNetwork[] {
    const suspiciousNetworks: TokenShillNetwork[] = [];

    for (const network of this.tokenNetworks.values()) {
      // Check if network meets suspicious criteria
      if (
        network.suspicionLevel > 0.6 &&
        network.networkAccounts.length >= 3 &&
        network.totalMentions >= 5
      ) {
        suspiciousNetworks.push(network);
      }
    }

    return suspiciousNetworks.sort((a, b) => b.suspicionLevel - a.suspicionLevel);
  }

  private generateTokenAlerts(networks: TokenShillNetwork[]): SuspiciousTokenAlert[] {
    const alerts: SuspiciousTokenAlert[] = [];

    for (const network of networks) {
      const alert = this.createTokenAlert(network);
      if (alert) {
        alerts.push(alert);
      }
    }

    return alerts;
  }

  private createTokenAlert(network: TokenShillNetwork): SuspiciousTokenAlert | null {
    // Determine alert type based on campaign type and indicators
    let alertType: SuspiciousTokenAlert['alertType'] = 'coordinated_shilling';
    let severity: SuspiciousTokenAlert['severity'] = 'medium';

    if (network.campaignType === 'pump_and_dump') {
      alertType = 'pump_campaign';
      severity = network.suspicionLevel > 0.8 ? 'high' : 'medium';
    } else if (network.campaignType === 'rugpull_preparation') {
      alertType = 'dump_incoming';
      severity = 'critical';
    }

    // Check for exit signals
    const hasExitSignals = network.indicators.some(i => i.type === 'exit_signals');
    if (hasExitSignals) {
      alertType = 'dump_incoming';
      severity = 'high';
    }

    // Calculate confidence
    const confidence = Math.min(1, network.suspicionLevel + 
      (network.indicators.length * 0.1) + 
      (network.networkAccounts.length * 0.05)
    );

    if (confidence < 0.5) {
      return null; // Not confident enough to generate alert
    }

    // Get bot accounts involved
    const botAccounts = network.networkAccounts.filter(username => {
      const botData = this.botTracker.getBotAccount(username);
      return botData && ['bot', 'suspicious', 'shill_farm'].includes(botData.reputation);
    });

    return {
      tokenSymbol: network.tokenSymbol,
      contractAddress: network.contractAddress,
      alertType,
      severity,
      confidence: Math.round(confidence * 100) / 100,
      detectedAt: new Date(),
      evidence: {
        botAccountsInvolved: botAccounts,
        totalShillAccounts: network.networkAccounts.length,
        timeframe: this.calculateTimeframe(network),
        patterns: this.extractPatterns(network),
        indicators: network.indicators
      },
      riskAssessment: this.calculateRiskAssessment(network, alertType),
      recommendations: this.generateRecommendations(network, alertType, severity)
    };
  }

  private calculateTimeframe(network: TokenShillNetwork): string {
    const duration = Date.now() - network.campaignStart.getTime();
    const hours = Math.floor(duration / (60 * 60 * 1000));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  }

  private extractPatterns(network: TokenShillNetwork): string[] {
    const patterns: string[] = [];

    if (network.campaignType === 'pump_and_dump') {
      patterns.push('Coordinated pump campaign');
    }
    
    if (network.indicators.some(i => i.type === 'shill_burst')) {
      patterns.push('Sudden burst of mentions');
    }

    if (network.indicators.some(i => i.type === 'fake_urgency')) {
      patterns.push('FOMO and urgency tactics');
    }

    if (network.networkAccounts.length > 10) {
      patterns.push('Large shill network');
    }

    const botCount = network.networkAccounts.filter(username => {
      const bot = this.botTracker.getBotAccount(username);
      return bot && bot.reputation === 'bot';
    }).length;

    if (botCount > network.networkAccounts.length * 0.5) {
      patterns.push('High bot account ratio');
    }

    return patterns;
  }

  private calculateRiskAssessment(
    network: TokenShillNetwork, 
    alertType: SuspiciousTokenAlert['alertType']
  ): SuspiciousTokenAlert['riskAssessment'] {
    let probabilityOfDump = 0;
    let timeToLikelyDump = 'unknown';
    let victimPotential: 'low' | 'medium' | 'high' = 'medium';

    if (alertType === 'pump_campaign') {
      probabilityOfDump = 0.7 + (network.suspicionLevel * 0.2);
      timeToLikelyDump = '6-48 hours';
      victimPotential = network.totalMentions > 50 ? 'high' : 'medium';
    } else if (alertType === 'dump_incoming') {
      probabilityOfDump = 0.9;
      timeToLikelyDump = '1-12 hours';
      victimPotential = 'high';
    } else if (alertType === 'coordinated_shilling') {
      probabilityOfDump = 0.4 + (network.suspicionLevel * 0.3);
      timeToLikelyDump = '24-72 hours';
      victimPotential = 'medium';
    }

    return {
      probabilityOfDump: Math.min(1, Math.round(probabilityOfDump * 100) / 100),
      timeToLikelyDump,
      victimPotential
    };
  }

  private generateRecommendations(
    network: TokenShillNetwork,
    alertType: SuspiciousTokenAlert['alertType'],
    severity: SuspiciousTokenAlert['severity']
  ): string[] {
    const recommendations: string[] = [];

    if (severity === 'critical' || alertType === 'dump_incoming') {
      recommendations.push('IMMEDIATE ACTION: Avoid buying, consider selling if holding');
      recommendations.push('Warn community about potential rugpull/exit scam');
    } else if (alertType === 'pump_campaign') {
      recommendations.push('Exercise extreme caution - likely pump and dump');
      recommendations.push('Monitor for dump signals in next 24-48 hours');
      recommendations.push('Do not FOMO buy based on social media hype');
    } else {
      recommendations.push('Investigate token fundamentals before investing');
      recommendations.push('Monitor bot activity levels and social sentiment');
    }

    recommendations.push(`Track ${network.networkAccounts.length} suspicious accounts involved`);
    
    if (network.contractAddress) {
      recommendations.push('Verify contract security and liquidity locks');
    }

    return recommendations;
  }

  private calculateTokenRiskLevels(): Array<{token: string; riskLevel: number; indicators: string[]}> {
    const riskTokens: Array<{token: string; riskLevel: number; indicators: string[]}> = [];

    for (const network of this.tokenNetworks.values()) {
      const indicators: string[] = [];
      let riskLevel = network.suspicionLevel;

      // Add specific risk indicators
      if (network.campaignType === 'pump_and_dump') {
        indicators.push('Pump and dump campaign detected');
        riskLevel += 0.2;
      }

      if (network.campaignType === 'rugpull_preparation') {
        indicators.push('Rugpull preparation detected');
        riskLevel += 0.3;
      }

      const botRatio = network.networkAccounts.filter(username => {
        const bot = this.botTracker.getBotAccount(username);
        return bot && bot.reputation === 'bot';
      }).length / Math.max(network.networkAccounts.length, 1);

      if (botRatio > 0.5) {
        indicators.push(`${Math.round(botRatio * 100)}% bot accounts involved`);
        riskLevel += 0.1;
      }

      if (network.totalMentions > 20) {
        indicators.push('High volume of coordinated mentions');
        riskLevel += 0.1;
      }

      riskLevel = Math.min(1, riskLevel);

      if (riskLevel > 0.3) {
        riskTokens.push({
          token: network.tokenSymbol,
          riskLevel: Math.round(riskLevel * 100) / 100,
          indicators
        });
      }
    }

    return riskTokens.sort((a, b) => b.riskLevel - a.riskLevel);
  }

  // Public API methods

  getActiveAlerts(): SuspiciousTokenAlert[] {
    return Array.from(this.activeAlerts.values());
  }

  getTokenNetwork(tokenSymbol: string): TokenShillNetwork | undefined {
    return this.tokenNetworks.get(tokenSymbol);
  }

  getAllTokenNetworks(): TokenShillNetwork[] {
    return Array.from(this.tokenNetworks.values());
  }

  getHighRiskTokens(): TokenShillNetwork[] {
    return Array.from(this.tokenNetworks.values())
      .filter(network => network.suspicionLevel > 0.7)
      .sort((a, b) => b.suspicionLevel - a.suspicionLevel);
  }

  clearExpiredAlerts(hoursOld: number = 24): number {
    const cutoff = new Date(Date.now() - hoursOld * 60 * 60 * 1000);
    let removedCount = 0;

    for (const [token, alert] of this.activeAlerts) {
      if (alert.detectedAt < cutoff) {
        this.activeAlerts.delete(token);
        removedCount++;
      }
    }

    logger.info('Cleared expired alerts', {
      operation: 'clear_expired_alerts',
      removedCount,
      hoursOld
    });

    return removedCount;
  }

  getNetworkStatistics(): {
    totalNetworks: number;
    activeAlerts: number;
    highRiskNetworks: number;
    averageAccountsPerNetwork: number;
    mostShilledTokens: Array<{token: string; mentions: number; accounts: number}>;
  } {
    const allNetworks = Array.from(this.tokenNetworks.values());
    const totalAccounts = allNetworks.reduce((sum, network) => sum + network.networkAccounts.length, 0);
    
    const mostShilledTokens = allNetworks
      .map(network => ({
        token: network.tokenSymbol,
        mentions: network.totalMentions,
        accounts: network.networkAccounts.length
      }))
      .sort((a, b) => b.mentions - a.mentions)
      .slice(0, 10);

    return {
      totalNetworks: allNetworks.length,
      activeAlerts: this.activeAlerts.size,
      highRiskNetworks: allNetworks.filter(n => n.suspicionLevel > 0.7).length,
      averageAccountsPerNetwork: Math.round(totalAccounts / Math.max(allNetworks.length, 1) * 100) / 100,
      mostShilledTokens
    };
  }
}